/**
 * PresenceHeader (06 §4.8) — conversation top bar. 1:1 shows presence subtitle
 * (online / last seen / typing); group shows members or typing. Right actions:
 * search + overflow (video/voice hidden in v1). Mobile adds a back chevron.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, MoreVertical } from 'lucide-react';
import type { Conversation } from '../../data/model';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { lastSeenText } from '../../lib/format';
import { Avatar } from '../primitives/Avatar';
import styles from './PresenceHeader.module.css';

/** Stable empty array so the zustand snapshot reference stays constant when no
 *  one is typing (avoids getSnapshot-not-cached loop). */
const NO_TYPING: string[] = [];

export function PresenceHeader({ conv }: { conv: Conversation }) {
  const navigate = useNavigate();
  const userById = useChatStore((s) => s.userById);
  const presence = useChatStore((s) => s.presence);
  const typingUsers = useChatStore((s) => s.typing[conv.id] ?? NO_TYPING);
  const openInfo = useUiStore((s) => s.openInfo);
  const setSearchOpen = useUiStore((s) => s.setSearchOpen);
  const toast = useUiStore((s) => s.toast);
  const [menuOpen, setMenuOpen] = useState(false);

  const peer = conv.type === 'direct' && conv.peerId ? userById(conv.peerId) : undefined;
  const title = conv.type === 'group' ? conv.title ?? 'Group' : peer?.displayName ?? 'Unknown';
  const avatarId = conv.type === 'group' ? conv.id : peer?.id ?? conv.id;

  let subtitle = '';
  if (typingUsers.length > 0) {
    subtitle =
      conv.type === 'group'
        ? `${userById(typingUsers[0]!)?.displayName?.split(' ')[0] ?? 'Someone'} is typing…`
        : 'typing…';
  } else if (conv.type === 'direct' && peer) {
    const p = presence[peer.id];
    subtitle = lastSeenText(p?.state ?? peer.presence, p?.lastSeenAtMs ?? peer.lastSeenAtMs);
  } else if (conv.type === 'group') {
    subtitle = conv.members
      .map((m) => (m.userId === useChatStore.getState().me.id ? 'You' : m.displayName.split(' ')[0]))
      .join(', ');
  }
  const subtitleIsLive = typingUsers.length > 0 || subtitle === 'online';

  return (
    <header className={styles.header}>
      <button
        className={`${styles.iconBtn} ${styles.back}`}
        onClick={() => navigate('/chats')}
        aria-label="Back"
      >
        <ArrowLeft size={22} />
      </button>

      <button className={styles.identity} onClick={() => openInfo(conv.id)}>
        <Avatar id={avatarId} name={title} url={conv.avatarUrl} size={40} />
        <span className={styles.text}>
          <span className={styles.name}>{title}</span>
          {subtitle && (
            <span className={`${styles.subtitle} ${subtitleIsLive ? styles.live : ''}`}>
              {subtitle}
            </span>
          )}
        </span>
      </button>

      <div className={styles.actions}>
        <button className={styles.iconBtn} onClick={() => setSearchOpen(true)} aria-label="Search in chat">
          <Search size={20} />
        </button>
        <div className={styles.menuWrap}>
          <button
            className={styles.iconBtn}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="More options"
            aria-haspopup="menu"
          >
            <MoreVertical size={20} />
          </button>
          {menuOpen && (
            <>
              <div className={styles.menuScrim} onClick={() => setMenuOpen(false)} />
              <ul className={styles.menu} role="menu">
                <li>
                  <button role="menuitem" onClick={() => { openInfo(conv.id); setMenuOpen(false); }}>
                    {conv.type === 'group' ? 'Group info' : 'Contact info'}
                  </button>
                </li>
                <li>
                  <button role="menuitem" onClick={() => { setSearchOpen(true); setMenuOpen(false); }}>
                    Search
                  </button>
                </li>
                <li>
                  <button role="menuitem" onClick={() => { toast('Muted notifications'); setMenuOpen(false); }}>
                    Mute notifications
                  </button>
                </li>
                <li>
                  <button role="menuitem" onClick={() => { toast('Chat cleared'); setMenuOpen(false); }}>
                    Clear chat
                  </button>
                </li>
                {conv.type === 'direct' && (
                  <li>
                    <button
                      role="menuitem"
                      className={styles.danger}
                      onClick={() => { toast('Contact blocked'); setMenuOpen(false); }}
                    >
                      Block
                    </button>
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
